import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';

import type {GuideLevelReports, ReportResults} from './CompareClasses.types';

import {transformReportResults} from './transformReportResults';

type CompareClassesActions = {
  setReports: (reportResults: ReportResults) => void;
  toggleResourceIds: (ids: string[], toggle: boolean) => void;
  setDisabled: (isDisabled: boolean) => void;
  setHasClasses: (hasClasses: boolean) => void;
  dequeue: () => void;
  reset: () => void;
};

export type CompareClassesState = {
  reports: GuideLevelReports; // used for compare classes
  // used to track which guide levels have been
  // toggled for comparison
  toggledResourceIds: {
    [resourceId: string]: boolean;
  };
  resourceQueue: string[][]; // used to queue up resource ids for report creation
  isDisabled: boolean; // used to disable the compare classes toggle if no classes
  hasClasses: boolean; // used in conjunction with isDisabled to determine if the reason for disabled is the teacher having classes + no access for some class OR no classes at all
};

const initialState: CompareClassesState = {
  reports: {},
  toggledResourceIds: {},
  isDisabled: false,
  hasClasses: false,
  resourceQueue: []
};

export type CompareClassesSlice = CompareClassesState & CompareClassesActions;

export const useCompareClassesStore = create<CompareClassesSlice>()(
  devtools(
    immer((set, get) => ({
      ...initialState,

      // transform incoming data into store format ReportInStore
      setReports: (reportResults = []) => {
        set((state) => {
          // set incoming ids
          const transformedReportResults = transformReportResults(reportResults);

          Object.entries(transformedReportResults).forEach(([resourceId, report]) => {
            state.reports[resourceId] = report;
          });
        });
      },

      toggleResourceIds: (ids, toggle) => {
        set((state) => {
          if (toggle) {
            ids.forEach((id) => {
              state.toggledResourceIds[id] = true;
            });
            state.resourceQueue.push(ids);
          } else {
            ids.forEach((id) => {
              delete state.toggledResourceIds[id];
            });
          }
        });
      },

      dequeue: () => {
        set((state) => {
          if (state.resourceQueue.length > 0) {
            state.resourceQueue.shift(); // remove the first item from the queue
          }
        });
      },

      setDisabled: (isDisabled) => {
        const {isDisabled: currentDisabled} = get();
        if (currentDisabled === isDisabled) {
          return;
        }
        set((state) => {
          state.isDisabled = isDisabled;
        });
      },

      setHasClasses: (hasClasses) => {
        set((state) => {
          state.hasClasses = hasClasses;
        });
      },

      reset: () => {
        set(() => ({
          ...initialState
        }));
      }
    }))
  )
);
